"use client";

import { Api } from "@/app/api";

export type CompaniesHouseStatus = {
	configured: boolean;
	baseUrl: string;
	environment: "sandbox" | "live" | string;
};

export type CompaniesHouseCompanySearchItem = {
	company_number?: string;
	company_status?: string;
	company_type?: string;
	/** Simple search */
	title?: string;
	/** Advanced search */
	company_name?: string;
	description?: string;
	address_snippet?: string;
	date_of_creation?: string;
	date_of_cessation?: string;
	sic_codes?: string[];
	registered_office_address?: Record<string, string | undefined>;
	links?: { self?: string; company_profile?: string };
	/** Enriched from company profile (not in search payload). */
	accountsType?: string;
	accountsMadeUpTo?: string;
};

export type CompaniesHouseSearchResponse = {
	items?: CompaniesHouseCompanySearchItem[];
	total_results?: number;
	items_per_page?: number;
	start_index?: number;
	kind?: string;
	hits?: number;
	appliedSicCodes?: string[];
	keywordSicMatches?: Array<{
		keyword: string;
		sicCodes: string[];
		label: string;
	}>;
	suggestedSicCodes?: Array<{
		code: string;
		description: string;
		fromKeyword?: string;
	}>;
	searchMode?: "single" | "name_or_sic";
	nameHits?: number;
	sicHits?: number;
	showingNewest?: boolean;
	page?: number;
	size?: number;
	hasMore?: boolean;
};

export type CompaniesHouseOfficerItem = {
	name?: string;
	officer_role?: string;
	appointed_on?: string;
	resigned_on?: string;
	nationality?: string;
	occupation?: string;
	country_of_residence?: string;
	address?: Record<string, string | undefined>;
	date_of_birth?: { month?: number; year?: number };
};

export type CompaniesHouseOfficersResponse = {
	items?: CompaniesHouseOfficerItem[];
	active_count?: number;
	resigned_count?: number;
	total_results?: number;
	items_per_page?: number;
	start_index?: number;
};

export type CompaniesHousePscItem = {
	name?: string;
	kind?: string;
	natures_of_control?: string[];
	notified_on?: string;
	ceased_on?: string;
	nationality?: string;
	country_of_residence?: string;
	address?: Record<string, string | undefined>;
	name_elements?: {
		forename?: string;
		middle_name?: string;
		surname?: string;
		title?: string;
	};
};

export type CompaniesHousePscResponse = {
	items?: CompaniesHousePscItem[];
	active_count?: number;
	ceased_count?: number;
	total_results?: number;
	items_per_page?: number;
	start_index?: number;
};

export type CompaniesHouseAdvancedSearchArgs = {
	companyNameIncludes?: string;
	companyNameExcludes?: string;
	companyStatus?: string;
	companyType?: string;
	sicCodes?: string;
	location?: string;
	incorporatedFrom?: string;
	incorporatedTo?: string;
	dissolvedFrom?: string;
	dissolvedTo?: string;
	size?: number;
	page?: number;
	startIndex?: number;
};

export const companiesHouseApiInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getCompaniesHouseStatus: builder.query<CompaniesHouseStatus, void>({
			query: () => ({ url: "/companies-house/status" }),
			providesTags: ["CompaniesHouse"],
		}),

		searchCompaniesHouse: builder.query<
			CompaniesHouseSearchResponse,
			{ q: string; itemsPerPage?: number; startIndex?: number }
		>({
			query: ({ q, itemsPerPage = 20, startIndex = 0 }) => ({
				url: "/companies-house/search",
				params: { q, itemsPerPage, startIndex },
			}),
			providesTags: ["CompaniesHouse"],
		}),

		searchCompaniesHouseOfficers: builder.query<
			CompaniesHouseSearchResponse,
			{ q: string; itemsPerPage?: number; startIndex?: number }
		>({
			query: ({ q, itemsPerPage = 20, startIndex = 0 }) => ({
				url: "/companies-house/search/officers",
				params: { q, itemsPerPage, startIndex },
			}),
			providesTags: ["CompaniesHouse"],
		}),

		advancedSearchCompaniesHouse: builder.query<
			CompaniesHouseSearchResponse,
			CompaniesHouseAdvancedSearchArgs
		>({
			query: (params) => ({
				url: "/companies-house/advanced-search",
				params,
			}),
			providesTags: ["CompaniesHouse"],
		}),

		getCompaniesHouseCompany: builder.query<Record<string, unknown>, string>({
			query: (companyNumber) => ({
				url: `/companies-house/companies/${encodeURIComponent(companyNumber)}`,
			}),
			providesTags: ["CompaniesHouse"],
		}),

		getCompaniesHouseOfficers: builder.query<
			CompaniesHouseOfficersResponse,
			{ companyNumber: string; startIndex?: number; itemsPerPage?: number }
		>({
			query: ({ companyNumber, startIndex = 0, itemsPerPage = 35 }) => ({
				url: `/companies-house/companies/${encodeURIComponent(companyNumber)}/officers`,
				params: { startIndex, itemsPerPage },
			}),
			providesTags: ["CompaniesHouse"],
		}),

		getCompaniesHousePscs: builder.query<
			CompaniesHousePscResponse,
			{ companyNumber: string; startIndex?: number; itemsPerPage?: number }
		>({
			query: ({ companyNumber, startIndex = 0, itemsPerPage = 35 }) => ({
				url: `/companies-house/companies/${encodeURIComponent(companyNumber)}/persons-with-significant-control`,
				params: { startIndex, itemsPerPage },
			}),
			providesTags: ["CompaniesHouse"],
		}),
	}),
});

export const {
	useGetCompaniesHouseStatusQuery,
	useLazySearchCompaniesHouseQuery,
	useLazySearchCompaniesHouseOfficersQuery,
	useLazyAdvancedSearchCompaniesHouseQuery,
	useLazyGetCompaniesHouseCompanyQuery,
	useLazyGetCompaniesHouseOfficersQuery,
	useLazyGetCompaniesHousePscsQuery,
} = companiesHouseApiInjection;
